// Social Feed Container Styles
// TikTok-inspired styling with mixed content support

import styled, { keyframes, css } from "styled-components";
import { theme } from "../theme";

// Animations
const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const slideUp = keyframes`
  from {
    transform: translateY(100%);
  }
  to {
    transform: translateY(0);
  }
`;

const pulse = keyframes`
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.7;
  }
`;

// Main Feed Container
export const FeedContainer = styled.div`
  position: relative;
  width: 100%;
  height: 100vh;
  background: ${theme.colors.background.primary};
  overflow: hidden;
  display: flex;
  flex-direction: column;

  @media (max-width: ${theme.breakpoints.mobile}) {
    height: 100dvh;
  }
`;

// Feed Header
export const FeedHeader = styled.header`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  background: ${theme.colors.background.overlay};
  backdrop-filter: blur(20px);
  border-bottom: 1px solid ${theme.colors.border.light};
  padding: ${theme.spacing.sm} ${theme.spacing.md};
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 60px;
  width: 100%;

  @media (max-width: ${theme.breakpoints.mobile}) {
    padding: ${theme.spacing.xs} ${theme.spacing.sm};
    min-height: 50px;
  }

  // Fixed-position elements ignore the page's own padding-left that normally
  // clears the persistent desktop sidebar, so it needs its own offset here
  // (same pattern as ProfileTopNavContainer).
  @media (min-width: calc(${theme.breakpoints.tablet} + 1px)) {
    left: 88px;
    width: calc(100% - 88px);
  }
`;

export const FeedTitle = styled.h1`
  font-size: 1.25rem;
  font-weight: 700;
  color: ${theme.colors.text.primary};
  margin: 0;

  @media (max-width: ${theme.breakpoints.mobile}) {
    font-size: 1.1rem;
  }
`;

export const FeedHeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
`;

export const HeaderButton = styled.button`
  background: ${theme.colors.background.secondary};
  border: 1px solid ${theme.colors.border.light};
  border-radius: ${theme.borderRadius.full};
  padding: ${theme.spacing.xs} ${theme.spacing.sm};
  color: ${theme.colors.text.primary};
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  backdrop-filter: blur(10px);

  &:hover {
    background: ${theme.colors.background.tertiary};
    border-color: ${theme.colors.border.orange};
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }

  @media (max-width: ${theme.breakpoints.mobile}) {
    padding: ${theme.spacing.xs};
    min-width: 40px;
    min-height: 40px;
  }
`;

// Feed Navigation Tabs
export const FeedNavigation = styled.nav`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 999;
  background: transparent;
  backdrop-filter: none;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  padding: 0 ${theme.spacing.md};
  overflow-x: auto;
  scrollbar-width: none;
  -ms-overflow-style: none;
  width: 100%;

  &::-webkit-scrollbar {
    display: none;
  }

  @media (max-width: ${theme.breakpoints.mobile}) {
    padding: 0 ${theme.spacing.sm};
  }

  // Fixed-position elements ignore the page's own padding-left that normally
  // clears the persistent desktop sidebar, so it needs its own offset here
  // (same pattern as ProfileTopNavContainer) — otherwise the leftmost tab
  // ("Nearby") renders directly under the sidebar.
  @media (min-width: calc(${theme.breakpoints.tablet} + 1px)) {
    left: 88px;
    width: calc(100% - 88px);
  }
`;

export const FeedTabs = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: ${theme.spacing.lg};
  min-width: max-content;
  padding: ${theme.spacing.sm} 0;

  @media (max-width: ${theme.breakpoints.mobile}) {
    gap: ${theme.spacing.md};
  }
`;

export const FeedTab = styled.button<{ $active: boolean }>`
  background: none;
  border: none;
  color: ${({ $active }) => 
    $active ? theme.colors.primary : 'rgba(255, 255, 255, 0.9)'};
  font-size: 0.875rem;
  font-weight: ${({ $active }) => $active ? 600 : 400};
  cursor: pointer;
  padding: ${theme.spacing.xs} ${theme.spacing.sm};
  border-radius: ${theme.borderRadius.md};
  transition: all 0.2s ease;
  position: relative;
  white-space: nowrap;
  display: flex;
  align-items: center;
  gap: ${theme.spacing.xs};
  /* Add text shadow for better readability over transparent background */
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);

  &:hover {
    color: ${({ $active }) => $active ? theme.colors.primary : 'white'};
    background: rgba(255, 255, 255, 0.1);
    text-shadow: 0 1px 3px rgba(0, 0, 0, 0.4);
  }

  &::after {
    content: '';
    position: absolute;
    bottom: -${theme.spacing.xs};
    left: 50%;
    transform: translateX(-50%);
    width: ${({ $active }) => $active ? '100%' : '0'};
    height: 2px;
    background: ${theme.colors.gradients.primary};
    transition: width 0.2s ease;
  }

  @media (max-width: ${theme.breakpoints.mobile}) {
    font-size: 0.8rem;
    padding: ${theme.spacing.xs};
  }
`;

export const TabIcon = styled.span`
  font-size: 1rem;
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    font-size: 0.9rem;
  }
`;

export const TabBadge = styled.span`
  background: ${theme.colors.gradients.primary};
  color: white;
  font-size: 0.7rem;
  font-weight: 600;
  padding: 2px 6px;
  border-radius: ${theme.borderRadius.full};
  min-width: 18px;
  text-align: center;
`;

// Feed Content Area
export const FeedContent = styled.div`
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  position: relative;
  scroll-behavior: smooth;
  scrollbar-width: none;
  -ms-overflow-style: none;
  padding: 0 16px;
  width: 100%;
  /* Add top padding to account for fixed headers */
  padding-top: 120px;

  &::-webkit-scrollbar {
    display: none;
  }

  @media (max-width: ${theme.breakpoints.mobile}) {
    padding-top: 100px; /* Account for smaller mobile headers */
  }
`;

// TikTok-like Feed Item Container
export const FeedItemContainer = styled.div<{ 
  $aspectRatio?: number; 
  $isFullScreen?: boolean;
  $isActive?: boolean;
}>`
  position: relative;
  width: 100%;
  height: ${({ $isFullScreen }) => $isFullScreen ? '100vh' : '400px'};
  min-height: ${({ $isFullScreen }) => $isFullScreen ? '100vh' : '400px'};
  background: ${theme.colors.background.card};
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  cursor: pointer;
  transition: transform 0.3s ease;
  border: 2px solid ${theme.colors.border.light};
  margin-bottom: 20px;
  border-radius: 16px;
  box-shadow: ${theme.shadows.light};

  &:hover {
    transform: scale(1.02);
    border-color: ${theme.colors.border.orange};
    box-shadow: ${theme.shadows.medium};
  }

  @media (max-width: ${theme.breakpoints.mobile}) {
    height: ${({ $isFullScreen }) => $isFullScreen ? '100dvh' : '350px'};
    min-height: ${({ $isFullScreen }) => $isFullScreen ? '100dvh' : '350px'};
  }
`;

// Feed Item Content
export const FeedItemContent = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  min-height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  background: ${theme.colors.background.card};
  border-radius: 0;
`;

// Media Container
export const MediaContainer = styled.div<{ aspectRatio?: number }>`
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  background: #000;
`;

export const MediaElement = styled.div<{ 
  type: 'image' | 'video' | 'mixed' | 'audio' | 'gif';
  aspectRatio?: number;
}>`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;

  ${({ type, aspectRatio }) => {
    if (type === 'image' && aspectRatio) {
      return `
        width: 100%;
        height: auto;
        max-height: 100%;
        object-fit: contain;
      `;
    }
    if (type === 'video') {
      return `
        width: 100%;
        height: 100%;
        object-fit: cover;
      `;
    }
    return `
      width: 100%;
      height: 100%;
    `;
  }}
`;

export const FeedImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
`;

export const FeedVideo = styled.video`
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
`;

// Content Overlay
export const ContentOverlay = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: linear-gradient(
    to top,
    rgba(255, 255, 255, 0.95) 0%,
    rgba(255, 255, 255, 0.8) 50%,
    transparent 100%
  );
  padding: ${theme.spacing.lg} ${theme.spacing.md} ${theme.spacing.md};
  color: ${theme.colors.text.primary};
  z-index: 10;

  @media (max-width: ${theme.breakpoints.mobile}) {
    padding: ${theme.spacing.md} ${theme.spacing.sm} ${theme.spacing.sm};
  }
`;

// User Info
export const UserInfo = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  margin-bottom: ${theme.spacing.sm};
`;

export const UserAvatar = styled.img`
  width: 40px;
  height: 40px;
  border-radius: ${theme.borderRadius.full};
  border: 2px solid rgba(255, 255, 255, 0.3);
  object-fit: cover;

  @media (max-width: ${theme.breakpoints.mobile}) {
    width: 35px;
    height: 35px;
  }
`;

export const UserDetails = styled.div`
  flex: 1;
`;

export const Username = styled.h3`
  font-size: 1rem;
  font-weight: 600;
  margin: 0 0 2px 0;
  color: white;

  @media (max-width: ${theme.breakpoints.mobile}) {
    font-size: 0.9rem;
  }
`;

export const Timestamp = styled.span`
  font-size: 0.8rem;
  color: rgba(255, 255, 255, 0.7);
  font-weight: 400;
`;

// Content Text
export const ContentText = styled.div`
  margin-bottom: ${theme.spacing.sm};
`;

export const ContentTitle = styled.h2`
  font-size: 1.1rem;
  font-weight: 600;
  margin: 0 0 ${theme.spacing.xs} 0;
  color: white;
  line-height: 1.3;

  @media (max-width: ${theme.breakpoints.mobile}) {
    font-size: 1rem;
  }
`;

export const ContentDescription = styled.p`
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.9);
  margin: 0;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;

  @media (max-width: ${theme.breakpoints.mobile}) {
    font-size: 0.85rem;
    -webkit-line-clamp: 2;
  }
`;

// Action Buttons (Right Side)
export const ActionButtons = styled.div`
  position: absolute;
  right: ${theme.spacing.md};
  bottom: ${theme.spacing.lg};
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.md};
  z-index: 20;

  @media (max-width: ${theme.breakpoints.mobile}) {
    right: ${theme.spacing.sm};
    bottom: ${theme.spacing.md};
    gap: ${theme.spacing.sm};
  }
`;

export const ActionButton = styled.button<{ 
  $variant?: 'primary' | 'secondary';
  $isActive?: boolean;
}>`
  width: 50px;
  height: 50px;
  border-radius: ${theme.borderRadius.full};
  border: none;
  background: ${({ $variant, $isActive }) => 
    $variant === 'primary' ? 
      ($isActive ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : 'rgba(255, 255, 255, 0.2)') :
      'rgba(255, 255, 255, 0.1)'};
  color: white;
  font-size: 1.2rem;
  cursor: pointer;
  transition: all 0.2s ease;
  backdrop-filter: blur(10px);
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);

  &:hover {
    transform: scale(1.1);
    background: ${({ $variant }) => 
      $variant === 'primary' ? 
        'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : 
        'rgba(255, 255, 255, 0.3)'};
  }

  &:active {
    transform: scale(0.95);
  }

  @media (max-width: ${theme.breakpoints.mobile}) {
    width: 45px;
    height: 45px;
    font-size: 1.1rem;
  }
`;

export const ActionCount = styled.span`
  font-size: 0.7rem;
  font-weight: 600;
  color: white;
  text-align: center;
  margin-top: 2px;
`;

// Swipe Mode Toggle
export const SwipeModeToggle = styled.button<{ $show: boolean }>`
  position: absolute;
  bottom: ${theme.spacing.md};
  left: 50%;
  transform: translateX(-50%);
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border: none;
  border-radius: ${theme.borderRadius.full};
  padding: ${theme.spacing.sm} ${theme.spacing.lg};
  color: white;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  opacity: ${({ $show }) => $show ? 1 : 0};
  visibility: ${({ $show }) => $show ? 'visible' : 'hidden'};
  transform: translateX(-50%) ${({ $show }) => $show ? 'translateY(0)' : 'translateY(20px)'};
  z-index: 30;
  box-shadow: 0 4px 20px rgba(102, 126, 234, 0.4);

  &:hover {
    transform: translateX(-50%) translateY(-2px);
    box-shadow: 0 6px 25px rgba(102, 126, 234, 0.6);
  }

  @media (max-width: ${theme.breakpoints.mobile}) {
    padding: ${theme.spacing.xs} ${theme.spacing.md};
    font-size: 0.8rem;
  }
`;

// Loading States
export const LoadingContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 200px;
  color: ${theme.colors.text.secondary};
`;

export const LoadingSpinner = styled.div`
  width: 40px;
  height: 40px;
  border: 3px solid rgba(102, 126, 234, 0.3);
  border-top: 3px solid #667eea;
  border-radius: 50%;
  animation: ${css`${pulse} 1s linear infinite`};
`;

export const LoadingText = styled.p`
  margin: ${theme.spacing.md} 0 0 0;
  font-size: 0.9rem;
  color: ${theme.colors.text.secondary};
`;

// Empty State
export const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 400px;
  text-align: center;
  color: ${theme.colors.text.secondary};
`;

export const EmptyStateIcon = styled.div`
  font-size: 3rem;
  margin-bottom: ${theme.spacing.md};
  opacity: 0.5;
`;

export const EmptyStateTitle = styled.h3`
  font-size: 1.2rem;
  font-weight: 600;
  margin: 0 0 ${theme.spacing.sm} 0;
  color: ${theme.colors.text.primary};
`;

export const EmptyStateDescription = styled.p`
  font-size: 0.9rem;
  margin: 0;
  max-width: 300px;
  line-height: 1.5;
`;

// Responsive Adjustments
export const MobileOptimizedContainer = styled.div`
  width: 100%;
  max-width: 100%;
  padding: 16px 0;
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    /* Mobile-specific optimizations */
    -webkit-overflow-scrolling: touch;
    overscroll-behavior: contain;
    
    /* Prevent zoom on double tap */
    touch-action: manipulation;
    
    /* Optimize for mobile performance */
    will-change: transform;
    transform: translateZ(0);
  }
`;

// Swipe Mode Overlay
export const SwipeModeOverlay = styled.div<{ isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.95);
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: ${({ isOpen }) => isOpen ? 1 : 0};
  visibility: ${({ isOpen }) => isOpen ? 'visible' : 'hidden'};
  transition: all 0.3s ease;
  animation: ${({ isOpen }) => isOpen ? css`${slideUp} 0.3s ease-out` : 'none'};
`;

export const SwipeModeContent = styled.div`
  width: 90%;
  max-width: 400px;
  background: ${theme.colors.background.secondary};
  border-radius: ${theme.borderRadius.xl};
  padding: ${theme.spacing.lg};
  text-align: center;
  color: white;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
`;

export const SwipeModeTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 700;
  margin: 0 0 ${theme.spacing.md} 0;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

export const SwipeModeDescription = styled.p`
  font-size: 1rem;
  margin: 0 0 ${theme.spacing.lg} 0;
  line-height: 1.5;
  color: rgba(255, 255, 255, 0.8);
`;

export const SwipeModeActions = styled.div`
  display: flex;
  gap: ${theme.spacing.md};
  justify-content: center;
`;

export const SwipeModeButton = styled.button<{ $variant: 'primary' | 'secondary' }>`
  padding: ${theme.spacing.md} ${theme.spacing.lg};
  border-radius: ${theme.borderRadius.lg};
  border: none;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  min-width: 120px;

  ${({ $variant }) => $variant === 'primary' ? `
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
    
    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 8px 25px rgba(102, 126, 234, 0.4);
    }
  ` : `
    background: rgba(255, 255, 255, 0.1);
    color: white;
    border: 1px solid rgba(255, 255, 255, 0.2);
    
    &:hover {
      background: rgba(255, 255, 255, 0.2);
    }
  `}

  &:active {
    transform: translateY(0);
  }
`;
